/**
 * Green Flag — SpotWaitlistedWidget
 *
 * Inline confirmation card shown on the Event details page when the user is
 * on the wait list. Mirrors SpotSavedWidget's structure but for the waitlist
 * state — the user has no confirmed spot to put on a calendar or invite
 * sparks to, so the only action is "Leave the wait list".
 */

import {
  Box,
  Button,
  Flex,
  Text,
  chakra,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { BottomDrawer } from "../BottomDrawer/BottomDrawer";
import { useState } from "react";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../lib/apiReq";
import { Icon } from "../Icon/Icon";

export interface SpotWaitlistedWidgetProps {
  sparkId: number;
  /** Called after the leave-waitlist request succeeds so the parent can refresh. */
  onLeftWaitlist: () => void;
}

export function SpotWaitlistedWidget({
  sparkId,
  onLeftWaitlist,
}: SpotWaitlistedWidgetProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [leaveReq, leaveReqState] = useReq(apiReq);

  const handleLeave = async () => {
    const result = await leaveReq.delete("/spark/waitlist", {
      spark_id: sparkId,
    });
    if (result.success) {
      setMoreOpen(false);
      onLeftWaitlist();
    }
  };

  return (
    <>
      <Box
        borderWidth="1px"
        borderColor="border.brand"
        borderRadius="xl"
        bg="bg.canvas"
        p="1rem"
        w="100%"
        maxW="28rem"
        mx="auto"
        boxShadow="sm"
      >
        <Flex align="flex-start" gap="0.75rem">
          <Flex
            w="2rem"
            h="2rem"
            borderRadius="full"
            bg="bg.brandSoft"
            align="center"
            justify="center"
            color="fg.onTint"
            flexShrink={0}
          >
            <Icon name="bell" boxSize="14px" />
          </Flex>
          <Box flex="1">
            <Text fontSize="1rem" fontWeight="bold">
              You're on the wait list
            </Text>
            <Text textStyle="caption" color="fg.muted" mt="0.125rem">
              We'll email you the moment a spot opens.
            </Text>
          </Box>
          <chakra.button
            type="button"
            aria-label="More options"
            bg="transparent"
            border="none"
            cursor="pointer"
            color="fg.muted"
            w="2rem"
            h="2rem"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            _hover={{ bg: "bg.subtle" }}
            onClick={() => setMoreOpen(true)}
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </chakra.button>
        </Flex>
      </Box>

      <BottomDrawer
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        title="More options"
      >
        <Button
          w="100%"
          bg="transparent"
          color="fg.danger"
          boxShadow="inset 0 0 0 1px var(--chakra-colors-fg-danger)"
          loading={leaveReqState.loading}
          onClick={handleLeave}
        >
          Leave the wait list
        </Button>
        {leaveReqState.error && (
          <Text mt="0.75rem" color="fg.danger" textStyle="caption">
            Something went wrong. Please try again.
          </Text>
        )}
      </BottomDrawer>
    </>
  );
}
